/**
 * The user's own settings — language, appearance, text size, default schedule view, density, and whether
 * Today shows phone numbers, and whether the Ask button shows — remembered per person (keyed by the signed-in user id) on this device. They
 * live under the gear (Settings) only. Regular (light) is the default appearance; Dark and Auto are
 * choices; the list is the default (accessible) view.
 *
 * 1.5.7: "Hide my number when I call from the app" (callMode) is kept on the server so it follows the person
 * (src/lib/calling.ts); the copy here is what the call links use while the app cannot reach it.
 *
 * 1.5.16: language, appearance, text size, the last program tab and the last Admin page follow the person too
 * (src/lib/my-settings.ts keeps them in step with the server). The copy here applies at once on every start —
 * no flash, and it works offline; a change is written here first and marked unsent until the server has it.
 * View, density, phone numbers on Today and the Ask button stay this device's own.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import i18n from '@/i18n';
import { browserLang } from './lang';
import { isCallMode, type CallMode } from '@shared/calling';
import { MY_SETTING_KEYS, knownSetting, type MySettingKey, type MySettingsValues } from '@shared/my-settings';

export type Theme = 'light' | 'dark' | 'auto';
export type TextSize = 'md' | 'lg';
export type { Lang } from './lang';
import type { Lang } from './lang';
export type ScheduleView = 'list' | 'calendar';
export type Density = 'comfortable' | 'compact';

export interface Prefs {
  lang: Lang;
  theme: Theme;
  size: TextSize;
  view: ScheduleView;
  density: Density;
  showPhones: boolean;
  /** The Ask button (0.9.5): anyone may hide it for themselves. */
  hideAssistant: boolean;
  /** 1.5.7: how call links dial (normal · hide, *67 · teams) — this device's copy of the person's server setting. */
  callMode: CallMode;
  /** The person changed callMode here and the server has not taken it yet (offline): this copy wins until it has. */
  callModeUnsent: boolean;
  /** 1.5.16: the program strip's last choice (null: not chosen yet — Housekeeping). */
  program: MySettingsValues['program'];
  /** 1.5.16: the last Admin page opened — one of the console's tab names, never an address. */
  adminPage: MySettingsValues['adminPage'];
  /** 1.5.16: the server's `updatedAt` this device last took or was answered with (null: never in step yet). */
  settingsAt: string | null;
  /** 1.5.16: which of the five were changed here and are not on the server yet: these win until they are. */
  settingsUnsent: MySettingKey[];
}

export const DEFAULT_PREFS: Prefs = { lang: 'en', theme: 'light', size: 'md', view: 'list', density: 'comfortable', showPhones: true, hideAssistant: false, callMode: 'normal', callModeUnsent: false, program: null, adminPage: null, settingsAt: null, settingsUnsent: [] };
const LAST = 'abf.prefs.last';
const keyFor = (userId: string | null) => `abf.prefs.${userId ?? 'device'}`;

export function readPrefs(userId: string | null): Prefs {
  try {
    const raw = localStorage.getItem(keyFor(userId)) ?? (userId ? null : localStorage.getItem(LAST));
    let p = raw ? (JSON.parse(raw) as Partial<Prefs>) : {};
    // 26 Oct 2026 (D-1025-WIRING): a person with nothing saved of their own yet keeps what was chosen on this device before sign-in
    // (the language on the sign-in page, the text size) — otherwise the first page that writes a preference (a program page
    // writing the last program) saved English over a language they had just chosen. Their program and Admin page stay their own.
    if (!raw && userId) {
      const device = localStorage.getItem(keyFor(null));
      if (device) {
        const d = JSON.parse(device) as Partial<Prefs>;
        p = { lang: d.lang, theme: d.theme, size: d.size, density: d.density };
      }
    }
    return {
      lang: p.lang === 'es' ? 'es' : p.lang === 'en' ? 'en' : browserLang(),
      theme: p.theme === 'dark' || p.theme === 'auto' ? p.theme : 'light',
      size: p.size === 'lg' ? 'lg' : 'md',
      view: p.view === 'calendar' ? 'calendar' : 'list',
      density: p.density === 'compact' ? 'compact' : 'comfortable',
      showPhones: p.showPhones !== false,
      hideAssistant: p.hideAssistant === true,
      callMode: isCallMode(p.callMode) ? p.callMode : 'normal',
      callModeUnsent: p.callModeUnsent === true,
      program: knownSetting('program', p.program),
      adminPage: knownSetting('adminPage', p.adminPage),
      settingsAt: typeof p.settingsAt === 'string' ? p.settingsAt : null,
      settingsUnsent: Array.isArray(p.settingsUnsent) ? MY_SETTING_KEYS.filter((k) => (p.settingsUnsent as unknown[]).includes(k)) : [],
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

/** Has this person chosen anything on this device yet? */
export function hasStoredPrefs(userId: string | null): boolean {
  try {
    return localStorage.getItem(keyFor(userId)) !== null;
  } catch {
    return false;
  }
}

export function applyPrefs(p: Prefs): void {
  const d = document.documentElement;
  d.setAttribute('data-theme', p.theme);
  if (p.size === 'lg') d.setAttribute('data-size', 'lg');
  else d.removeAttribute('data-size');
  if (p.density === 'compact') d.setAttribute('data-density', 'compact');
  else d.removeAttribute('data-density');
  d.setAttribute('lang', p.lang);
  if (i18n.language !== p.lang) void i18n.changeLanguage(p.lang);
}

interface PrefsCtx {
  prefs: Prefs;
  /**
   * Saves a change on this device. A change to one of the five that follow the person is marked unsent (and
   * sent by src/lib/my-settings.ts) — unless the patch says what is unsent itself (`settingsUnsent`), which is
   * how the server's own answer is taken without sending it back.
   */
  setPrefs: (patch: Partial<Prefs>) => void;
  userId: string | null;
}

const Ctx = createContext<PrefsCtx | null>(null);

export function PrefsProvider({ userId, children }: { userId: string | null; children: ReactNode }) {
  const [prefs, setState] = useState<Prefs>(() => readPrefs(userId));
  // whose settings `prefs` holds. A page's own effect runs before this provider's effect, so on the commit where the person becomes
  // known a page can press setPrefs (the last program) while `prefs` is still the signed-out device's: that must build on the
  // person's saved choices, not write the device's English over their Spanish (1101).
  const loadedFor = useRef<string | null>(userId);

  useEffect(() => {
    loadedFor.current = userId;
    setState(readPrefs(userId));
  }, [userId]);

  useEffect(() => {
    applyPrefs(prefs);
  }, [prefs]);

  const setPrefs = useCallback(
    (patch: Partial<Prefs>) => {
      setState((held) => {
        const prev = loadedFor.current === userId ? held : readPrefs(userId);
        const next = { ...prev, ...patch };
        if (!('settingsUnsent' in patch)) {
          const changed = MY_SETTING_KEYS.filter((k) => k in patch && patch[k] !== prev[k]);
          if (changed.length) next.settingsUnsent = MY_SETTING_KEYS.filter((k) => prev.settingsUnsent.includes(k) || changed.includes(k));
        }
        try {
          localStorage.setItem(keyFor(userId), JSON.stringify(next));
          localStorage.setItem(LAST, JSON.stringify(next));
        } catch {
          /* private mode: settings last for this visit */
        }
        return next;
      });
    },
    [userId],
  );

  const value = useMemo(() => ({ prefs, setPrefs, userId }), [prefs, setPrefs, userId]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePrefs(): PrefsCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('usePrefs outside PrefsProvider');
  return v;
}
